/**
 * Pure types/functions for config-driven menus (no network deps — tested
 * directly). The loading hook lives in useConfigMenus.ts.
 */

export interface ConfigMenuItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  children?: ConfigMenuItem[];
}

export interface ConfigMenu {
  name: string;
  label: string;
  icon?: string;
  order: number;
  module?: string;
  items: ConfigMenuItem[];
}

/** Generic nav node shape (MainLayout's NavItem is structurally compatible). */
export interface ConfigNavItem<TIcon> {
  text: string;
  icon: TIcon;
  path: string;
  /** Expand/React key when `path` is empty (subsystem sections and kind groups). */
  id?: string;
  /** Kind-group chrome (`catalogs` / `documents` / …) — translated at render. */
  kind?: string;
  children?: ConfigNavItem<TIcon>[];
}

export function getUiLocale(): string {
  const raw = (typeof localStorage !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  return raw.split('-')[0] || 'en';
}

function itemToNav<TIcon>(
  item: ConfigMenuItem,
  resolveIcon: (name: string | undefined) => TIcon,
): ConfigNavItem<TIcon> {
  const children = (item.children || []).map((ch) => itemToNav(ch, resolveIcon));
  return {
    text: item.label,
    icon: resolveIcon(item.icon),
    path: item.route || '',
    ...(children.length > 0 ? { children } : {}),
  };
}

/**
 * Pure converter: config menus → nav sections. A menu becomes a section whose
 * children are its items; items with children become nested groups.
 */
export function configMenusToNavItems<TIcon>(
  menus: ConfigMenu[],
  resolveIcon: (name: string | undefined) => TIcon,
): ConfigNavItem<TIcon>[] {
  return menus.map((menu) => ({
    text: menu.label,
    icon: resolveIcon(menu.icon),
    path: menu.items[0]?.route || '',
    children: menu.items.map((item) => itemToNav(item, resolveIcon)),
  }));
}
